/**
 * WorkspaceStudioPage – Home Designer (Phase 4 Shell MVP).
 *
 * Layout:
 *   ┌─────────────────────────────────────────────────────┐
 *   │  StudioHeader (layout name · save status · publish · exit)     │
 *   ├──────────────┬──────────────────────────────────────┤
 *   │  StudioSidebar │  StudioCanvas (shared renderer)    │
 *   │  (layout       │  ← SAME hooks + components as      │
 *   │   list/CRUD)   │    HomeLauncherPage                 │
 *   └──────────────┴──────────────────────────────────────┘
 *
 * Home Designer always operates in draft-edit mode.
 * Runtime homepage always loads published version (read-only for assigned users).
 *
 * NOTE: "Home Designer" = launcher/home screen designer.
 * Future "Workspaces" terminology is reserved for collaborative operational spaces.
 */
import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth }             from '../../contexts/AuthContext';
import { useDealerAdmin }      from '../../contexts/DealerAdminContext';
import { readSupportedTenantSession } from '../Admin/supportedTenantsSession';
import { ROUTES }              from '../../utils/constants';
import useStudioWorkspace      from '../../hooks/workspaceStudio/useStudioWorkspace';
import StudioHeader            from './components/StudioHeader';
import StudioSidebar           from './components/StudioSidebar';
import StudioCanvas            from './components/StudioCanvas';
import PublishConfirmDialog    from './components/PublishConfirmDialog';
import ImportLauncherDialog    from './components/ImportLauncherDialog';

export default function WorkspaceStudioPage() {
  const { workspaceId }          = useParams();
  const { currentUser, tenantId } = useAuth();
  const dealerAdmin              = useDealerAdmin();
  const navigate                 = useNavigate();
  const importTenantId           = dealerAdmin.tenantId
    || readSupportedTenantSession()?.tenantId
    || tenantId;

  const {
    workspace,
    runtimeContext,
    rawLayoutData,
    initialIcons,
    isLoading,
    error,
    publish,
    isPublishing,
    publishError,
  } = useStudioWorkspace(workspaceId || null);

  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const layoutSnapshotRef = useRef(null);

  const handlePublishConfirm = async () => {
    const ok = await publish(layoutSnapshotRef.current);
    if (ok) setPublishDialogOpen(false);
  };

  const handleExit = () => {
    navigate(ROUTES.ADMIN_WORKSPACES);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>

      <StudioHeader
        workspaceName={workspace?.name}
        onPublish={() => setPublishDialogOpen(true)}
        onExit={handleExit}
        isPublishing={isPublishing}
        hasDraftVersion={Boolean(workspace?.currentDraftVersionId)}
      />

      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>

        <StudioSidebar
          selectedWorkspaceId={workspaceId}
          onImportLauncher={() => setImportDialogOpen(true)}
        />

        {/* Main canvas area */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
          {!workspaceId && (
            <Box sx={{ m: 'auto', textAlign: 'center', p: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Select a layout to start editing
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Choose a layout from the sidebar, or create a new one.
              </Typography>
            </Box>
          )}

          {workspaceId && isLoading && (
            <Box sx={{ m: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                Loading layout…
              </Typography>
            </Box>
          )}

          {workspaceId && !isLoading && error && (
            <Box sx={{ m: 'auto', textAlign: 'center', p: 4 }}>
              <Typography variant="h6" color="error" gutterBottom>
                Failed to load layout
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {error}
              </Typography>
            </Box>
          )}

          {workspaceId && !isLoading && !error && workspace && (
            <StudioCanvas
              key={workspaceId}
              runtimeContext={runtimeContext}
              initialIcons={initialIcons}
              rawLayoutData={rawLayoutData}
              uid={currentUser?.uid}
              tenantId={importTenantId}
              onLayoutSnapshot={(snapshot) => {
                layoutSnapshotRef.current = snapshot;
              }}
            />
          )}
        </Box>
      </Box>

      <PublishConfirmDialog
        open={publishDialogOpen}
        onClose={() => setPublishDialogOpen(false)}
        onConfirm={handlePublishConfirm}
        workspaceName={workspace?.name}
        isPublishing={isPublishing}
        publishError={publishError}
      />

      <ImportLauncherDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        targetTenantId={importTenantId}
      />
    </Box>
  );
}
